import type { OpenResult, WorkerRequest, WorkerResponse } from "./protocol";
import type { Row, SqlDriver, SqlParam, Statement } from "./types";

type Pending = { resolve: (value: WorkerResponse & { ok: true }) => void; reject: (error: Error) => void };

/** Distributes over the union so each request keeps its own fields (Omit alone would collapse it). */
type WithoutId<T> = T extends unknown ? Omit<T, "id"> : never;

/** SqlDriver over the database worker: each call is one message and one reply. */
export class WorkerSqlDriver implements SqlDriver {
  private readonly worker: Worker;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;

  constructor() {
    this.worker = new Worker(new URL("./sqlite.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const reply = event.data;
      const entry = this.pending.get(reply.id);
      if (!entry) return;
      this.pending.delete(reply.id);
      if (reply.ok) entry.resolve(reply);
      else {
        const error = new Error(reply.error.message);
        error.name = reply.error.name;
        entry.reject(error);
      }
    };
    this.worker.onerror = (event) => {
      const error = new Error(event.message || "database worker crashed");
      for (const entry of this.pending.values()) entry.reject(error);
      this.pending.clear();
    };
  }

  private call(request: WithoutId<WorkerRequest>): Promise<WorkerResponse & { ok: true }> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ ...request, id } as WorkerRequest);
    });
  }

  async open(userId: string): Promise<OpenResult> {
    return (await this.call({ method: "open", userId })).result as OpenResult;
  }

  async query<T extends Row = Row>(sql: string, params?: readonly SqlParam[]): Promise<T[]> {
    return (await this.call({ method: "query", sql, params })).result as T[];
  }

  async run(sql: string, params?: readonly SqlParam[]): Promise<void> {
    await this.call({ method: "run", sql, params });
  }

  async batch(statements: readonly Statement[]): Promise<void> {
    await this.call({ method: "batch", statements });
  }

  async close(): Promise<void> {
    await this.call({ method: "close" });
  }

  /** Deletes the open database's file. */
  async destroy(): Promise<void> {
    await this.call({ method: "destroy" });
  }

  terminate(): void {
    this.worker.terminate();
    for (const entry of this.pending.values()) entry.reject(new Error("database worker terminated"));
    this.pending.clear();
  }
}
